<script setup lang="ts" generic="T extends object">
  import type { StyleValue } from "vue";
  import { ChevronDown, GripVertical } from "lucide-vue-next";
  import { TableCell, TableRow } from "@/components/ui/table";
  import type { DataTableColumn } from "./types";

  defineProps<{
    item: T;
    columns: DataTableColumn<T>[];
    state?: "open" | "selected";
    expandable: boolean;
    handle: "none" | "hidden" | "visible";
    clickable: boolean;
    rowDataCy?: string;
    columnStyle: (column: DataTableColumn<T>) => StyleValue;
    cellValue: (item: T, column: DataTableColumn<T>) => unknown;
  }>();

  const emit = defineEmits<{ click: [event: MouseEvent | KeyboardEvent] }>();

  defineSlots<{
    [name: `cell-${string}`]: (props: { item: T; value: unknown }) => unknown;
    actions: (props: { item: T }) => unknown;
  }>();
</script>

<template>
  <TableRow
    :data-cy="rowDataCy"
    :data-state="state"
    :tabindex="clickable ? 0 : undefined"
    :class="state === 'open' && 'border-b-0 bg-background'"
    @click="emit('click', $event)"
    @auxclick="$event.button === 1 && emit('click', $event)"
    @keydown.enter.self="emit('click', $event)">
    <TableCell
      v-if="expandable"
      class="w-9 pr-0">
      <ChevronDown
        class="size-4 text-muted-foreground transition-transform duration-200"
        :class="state !== 'open' && '-rotate-90'" />
    </TableCell>
    <TableCell
      v-for="(column, columnIndex) in columns"
      :key="column.key"
      :class="[column.class, column.align === 'right' && 'text-right']"
      :style="columnStyle(column)">
      <div
        v-if="columnIndex === 0 && handle !== 'none'"
        class="flex items-center gap-2">
        <GripVertical
          class="size-3.5 shrink-0 text-foreground-subtle"
          :class="handle === 'visible' ? 'row-handle cursor-grab active:cursor-grabbing' : 'invisible'" />
        <div class="min-w-0 flex-1">
          <slot
            :name="`cell-${column.key}`"
            :item="item"
            :value="cellValue(item, column)">
            <span class="line-clamp-1">{{ cellValue(item, column) }}</span>
          </slot>
        </div>
      </div>
      <slot
        v-else
        :name="`cell-${column.key}`"
        :item="item"
        :value="cellValue(item, column)">
        <span class="line-clamp-1">{{ cellValue(item, column) }}</span>
      </slot>
    </TableCell>
    <TableCell v-if="$slots.actions">
      <slot
        name="actions"
        :item="item" />
    </TableCell>
  </TableRow>
</template>
